/**
 * @module CISContext
 * @description Contexto global para la gestión del framework CIS Controls v8.
 *
 * Administra el estado completo de los 18 controles CIS y sus 153 salvaguardas,
 * organizadas en tres Grupos de Implementación (IG1, IG2, IG3) según el nivel
 * de madurez y recursos de la organización.
 *
 * Estrategia de carga de datos:
 * 1. Intenta cargar desde las tablas `cis_controls`, `cis_salvaguardas`, etc. en Supabase.
 * 2. Si la BD no está disponible o no tiene datos, usa los datos mock de `cisMockData.ts`.
 * 3. Espera a que `dbUserReady` sea `true` antes de hacer queries, garantizando que
 *    `organization_id` provenga de `public.users` y no solo de los metadatos de Auth.
 *
 * El patrón de actualización es optimista: los cambios se reflejan en el estado local
 * de inmediato y luego se sincronizan con la BD. Si la BD falla, se revierte recargando.
 *
 * @author Sistema ACSGC
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';
import {
  mockCISAuditTrail,
  calculateCISMetrics,
  getCISControlsWithMetrics,
  getAllCISSalvaguardas,
} from '../data/cisMockData';
import type {
  CISControl,
  CISSalvaguarda,
  CISTask,
  CISEvidence,
  CISAuditEntry,
  CISMetrics,
  CISImplementationGroup,
} from '../types/cis';

// =====================================================
// INTERFACES
// =====================================================

/**
 * Contrato del contexto CIS Controls v8.
 *
 * @property controls - Lista de los 18 controles CIS con sus salvaguardas y métricas.
 * @property salvaguardas - Todas las salvaguardas aplanadas (derivadas de `controls`).
 * @property auditTrail - Historial de cambios de estado de salvaguardas.
 * @property isLoading - `true` durante la carga inicial o recarga de datos.
 * @property error - Último mensaje de error; null si no hay error activo.
 * @property isDemoMode - `true` si los datos provienen del mock local en lugar de Supabase.
 * @property selectedSalvaguarda - Salvaguarda actualmente seleccionada en la UI.
 * @property setSelectedSalvaguarda - Establece la salvaguarda seleccionada (o null para deseleccionar).
 * @property metrics - Métricas calculadas del estado global de cumplimiento CIS.
 * @property updateSalvaguarda - Actualiza campos de una salvaguarda (estado, puntuación, notas).
 * @property assignReferents - Asigna referentes técnicos a una salvaguarda.
 * @property addTask - Agrega una tarea a una salvaguarda específica.
 * @property updateTask - Actualiza una tarea existente.
 * @property addEvidence - Agrega evidencia de cumplimiento a una salvaguarda.
 * @property refreshData - Fuerza recarga completa desde Supabase o mock.
 * @property getSalvaguardasByControl - Filtra salvaguardas por ID de control padre.
 * @property getControlById - Busca un control por su UUID.
 * @property getSalvaguardaById - Busca una salvaguarda por su UUID.
 * @property getSalvaguardasByIG - Filtra salvaguardas aplicables a un grupo de implementación.
 */
interface CISContextType {
  controls: CISControl[];
  salvaguardas: CISSalvaguarda[];
  auditTrail: CISAuditEntry[];
  isLoading: boolean;
  error: string | null;
  isDemoMode: boolean;
  selectedSalvaguarda: CISSalvaguarda | null;
  setSelectedSalvaguarda: (s: CISSalvaguarda | null) => void;
  metrics: CISMetrics;
  updateSalvaguarda: (id: string, updates: Partial<CISSalvaguarda>) => Promise<void>;
  assignReferents: (salvaguardaId: string, referentIds: string[]) => Promise<void>;
  addTask: (salvaguardaId: string, task: Omit<CISTask, 'id'>) => Promise<void>;
  updateTask: (salvaguardaId: string, taskId: string, updates: Partial<CISTask>) => Promise<void>;
  addEvidence: (salvaguardaId: string, evidence: Omit<CISEvidence, 'id'>) => Promise<void>;
  refreshData: () => Promise<void>;
  getSalvaguardasByControl: (controlId: string) => CISSalvaguarda[];
  getControlById: (id: string) => CISControl | undefined;
  getSalvaguardaById: (id: string) => CISSalvaguarda | undefined;
  getSalvaguardasByIG: (ig: CISImplementationGroup) => CISSalvaguarda[];
}

/** Contexto interno — accesible solo mediante `useCIS()`. */
const CISContext = createContext<CISContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface CISProviderProps {
  children: React.ReactNode;
}

/**
 * Proveedor del contexto CIS Controls v8.
 *
 * Gestiona el ciclo de vida completo de los datos CIS: carga, actualización optimista,
 * sincronización con BD y fallback a mock. Espera a `dbUserReady` antes de hacer
 * queries para garantizar que `organization_id` sea confiable.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function CISProvider({ children }: CISProviderProps) {
  const { user, dbUserReady } = useAuth();

  // ── Estado principal ────────────────────────────────────────────────────────
  const [controls, setControls] = useState<CISControl[]>([]);
  const [auditTrail, setAuditTrail] = useState<CISAuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [selectedSalvaguarda, setSelectedSalvaguarda] = useState<CISSalvaguarda | null>(null);

  /**
   * Todas las salvaguardas como lista plana derivada de los controles.
   * Se memoiza para evitar re-computaciones innecesarias cuando otros campos del
   * estado cambian sin afectar la lista de controles.
   */
  const salvaguardas = React.useMemo(
    () => controls.flatMap(c => c.salvaguardas),
    [controls]
  );

  // ─────────────────────────────────────────────────────
  // CARGAR DATOS
  // ─────────────────────────────────────────────────────

  /**
   * Intenta cargar los datos CIS desde Supabase con joins anidados:
   * controles → salvaguardas → tareas/evidencias.
   *
   * El cliente supabase se castea como `any` porque las tablas `cis_*` no están
   * incluidas en los tipos generados automáticamente (`database.types.ts`).
   *
   * @returns `true` si los datos se cargaron con éxito; `false` si falló o no hay datos.
   */
  const loadFromDatabase = useCallback(async (): Promise<boolean> => {
    if (!user?.organization_id) {
      logger.warn('[CIS] No hay organización del usuario');
      return false;
    }

    try {
      logger.info('[CIS] Cargando datos desde BD...');

      // Cast necesario: tablas cis_* no están en database.types.ts generado
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;

      const { data: controlsData, error: ctrlError } = await db
        .from('cis_controls')
        .select(`
          *,
          cis_salvaguardas (
            *,
            cis_tasks (*),
            cis_evidence (*)
          )
        `)
        .eq('organization_id', user.organization_id)
        .order('number');

      if (ctrlError) throw ctrlError;

      if (controlsData && controlsData.length > 0) {
        // Transformar respuesta de BD (snake_case + relaciones anidadas) a tipos TypeScript
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const transformedControls: CISControl[] = controlsData.map((c: any) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const salv: CISSalvaguarda[] = (c.cis_salvaguardas || []).map((s: any) => ({
            id: s.id,
            controlId: s.control_id,
            number: s.number,
            title: s.title,
            description: s.description || '',
            ig1: s.ig1 || false,
            ig2: s.ig2 || false,
            ig3: s.ig3 || false,
            assetType: s.asset_type,
            securityFunction: s.security_function,
            status: s.status || 'not_started',
            complianceScore: s.compliance_score || 0,
            assignedReferents: [],
            tasks: (s.cis_tasks || []).map((t: any) => ({
              id: t.id,
              salvaguardaId: t.salvaguarda_id,
              title: t.title,
              description: t.description,
              status: t.status || 'pending',
              priority: t.priority || 'medium',
              assignedTo: t.assigned_to,
              dueDate: t.due_date,
              completedAt: t.completed_at,
              checklist: [],
            })),
            evidence: (s.cis_evidence || []).map((e: any) => ({
              id: e.id,
              salvaguardaId: e.salvaguarda_id,
              fileName: e.title || e.file_url || 'Evidencia',
              description: e.description,
              uploadedBy: e.uploaded_by,
              isVerified: false,
              createdAt: e.created_at,
            })),
            notes: s.notes,
            lastUpdated: s.updated_at,
          }));

          // Calcular métricas de cumplimiento del control en base a sus salvaguardas
          const implemented = salv.filter(s => s.status === 'implemented').length;
          const inProgress = salv.filter(s => s.status === 'in_progress').length;
          const pct = salv.length > 0
            ? Math.round(salv.reduce((acc, s) => acc + s.complianceScore, 0) / salv.length)
            : 0;

          return {
            id: c.id,
            number: c.number,
            title: c.title,
            description: c.description || '',
            securityFunction: c.security_function,
            salvaguardas: salv,
            totalSalvaguardas: salv.length,
            implemented,
            inProgress,
            compliancePercentage: pct,
          };
        });

        setControls(transformedControls);
        setIsDemoMode(false);
        logger.info(`[CIS] ✅ Cargados ${transformedControls.length} controles`);
        return true;
      }

      return false;
    } catch (err) {
      logger.error('[CIS] Error cargando desde BD:', err);
      return false;
    }
  }, [user?.organization_id]);

  /**
   * Carga los datos CIS desde el mock local cuando Supabase no está disponible.
   * Activa `isDemoMode` para indicar al resto de la UI que los datos son de ejemplo.
   */
  const loadMockData = useCallback(() => {
    logger.info('[CIS] 🎭 Cargando datos mock...');
    const ctls = getCISControlsWithMetrics();
    setControls(ctls);
    setAuditTrail(mockCISAuditTrail);
    setIsDemoMode(true);
    logger.info(`[CIS] ✅ Mock: ${ctls.length} controles, ${ctls.flatMap(c => c.salvaguardas).length} salvaguardas`);
  }, []);

  /**
   * Orquesta la carga de datos: primero intenta BD, si falla usa mock.
   * Gestiona los estados `isLoading` y `error` durante todo el proceso.
   */
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loadedFromDb = await loadFromDatabase();
      if (!loadedFromDb) {
        loadMockData();
      }
    } catch (err) {
      logger.error('[CIS] Error general:', err);
      setError('Error al cargar datos CIS Controls v8');
      loadMockData();
    } finally {
      setIsLoading(false);
    }
  }, [loadFromDatabase, loadMockData]);

  /**
   * Efecto de carga inicial.
   * Espera `dbUserReady = true` para garantizar que `organization_id` venga de
   * `public.users` (la tabla real), no de los metadatos del token de Auth de Supabase.
   * Si no hay sesión activa, muestra datos mock directamente.
   */
  useEffect(() => {
    if (dbUserReady && user?.organization_id) {
      refreshData();
    } else if (!user && !dbUserReady) {
      // No hay sesión: mostrar mock
      loadMockData();
      setIsLoading(false);
    }
  }, [dbUserReady, user?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────
  // MÉTRICAS
  // ─────────────────────────────────────────────────────

  /**
   * Métricas globales de cumplimiento CIS calculadas en tiempo real.
   * Se recalculan automáticamente cada vez que cambia la lista de controles.
   */
  const metrics = React.useMemo(
    () => calculateCISMetrics(controls),
    [controls]
  );

  // ─────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────

  /** Retorna todas las salvaguardas que pertenecen a un control específico. */
  const getSalvaguardasByControl = useCallback(
    (controlId: string) => salvaguardas.filter(s => s.controlId === controlId),
    [salvaguardas]
  );

  /** Busca un control CIS por su UUID. */
  const getControlById = useCallback(
    (id: string) => controls.find(c => c.id === id),
    [controls]
  );

  /** Busca una salvaguarda CIS por su UUID. */
  const getSalvaguardaById = useCallback(
    (id: string) => salvaguardas.find(s => s.id === id),
    [salvaguardas]
  );

  /**
   * Filtra salvaguardas aplicables a un Grupo de Implementación específico.
   * IG1 = salvaguardas básicas; IG2 = intermedias; IG3 = avanzadas.
   * Una salvaguarda puede pertenecer a múltiples grupos.
   *
   * @param ig - Grupo de implementación: 'IG1', 'IG2' o 'IG3'.
   */
  const getSalvaguardasByIG = useCallback(
    (ig: CISImplementationGroup) => {
      const key = ig.toLowerCase() as 'ig1' | 'ig2' | 'ig3';
      return salvaguardas.filter(s => s[key]);
    },
    [salvaguardas]
  );

  // ─────────────────────────────────────────────────────
  // ACTUALIZAR ESTADO INTERNO
  // ─────────────────────────────────────────────────────

  /**
   * Actualiza una salvaguarda dentro del árbol de controles (estado local).
   * También actualiza `selectedSalvaguarda` si corresponde al mismo ID,
   * para mantener consistencia con el panel de detalle activo.
   *
   * @param salvaguardaId - UUID de la salvaguarda a actualizar.
   * @param updates - Campos a actualizar en la salvaguarda.
   */
  const updateControlsState = useCallback((salvaguardaId: string, updates: Partial<CISSalvaguarda>) => {
    setControls(prev => prev.map(ctrl => ({
      ...ctrl,
      salvaguardas: ctrl.salvaguardas.map(s =>
        s.id === salvaguardaId ? { ...s, ...updates } : s
      ),
    })));

    if (selectedSalvaguarda?.id === salvaguardaId) {
      setSelectedSalvaguarda(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [selectedSalvaguarda]);

  // ─────────────────────────────────────────────────────
  // ACCIONES CRUD
  // ─────────────────────────────────────────────────────

  /**
   * Actualiza el estado, puntuación de cumplimiento y/o notas de una salvaguarda.
   * Usa actualización optimista: actualiza el estado local inmediatamente y luego
   * sincroniza con Supabase. Si la BD falla, revierte recargando desde el servidor.
   *
   * En modo demo, la actualización solo afecta el estado local.
   *
   * @param id - UUID de la salvaguarda a actualizar.
   * @param updates - Campos a actualizar (status, complianceScore, notes).
   */
  const updateSalvaguarda = useCallback(async (
    id: string,
    updates: Partial<CISSalvaguarda>
  ) => {
    // Actualización optimista: el usuario ve el cambio inmediatamente
    updateControlsState(id, updates);

    if (!isDemoMode && user?.organization_id) {
      try {
        const dbUpdates: Record<string, unknown> = {};
        if (updates.status) dbUpdates.status = updates.status;
        if (updates.complianceScore !== undefined) dbUpdates.compliance_score = updates.complianceScore;
        if (updates.notes !== undefined) dbUpdates.notes = updates.notes;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;
        const { error: dbError } = await db
          .from('cis_salvaguardas')
          .update({ ...dbUpdates, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (dbError) throw dbError;
        logger.info(`[CIS] ✅ Salvaguarda ${id} actualizada`);
      } catch (err) {
        logger.error('[CIS] Error actualizando salvaguarda:', err);
        // Revertir el cambio optimista recargando desde BD
        await refreshData();
      }
    }
  }, [isDemoMode, user?.organization_id, updateControlsState, refreshData]);

  /**
   * Asigna referentes técnicos a una salvaguarda, reemplazando cualquier
   * asignación previa (elimina todas y luego inserta las nuevas).
   *
   * @param salvaguardaId - UUID de la salvaguarda.
   * @param referentIds - Lista de UUIDs de los referentes a asignar.
   */
  const assignReferents = useCallback(async (
    salvaguardaId: string,
    referentIds: string[]
  ) => {
    updateControlsState(salvaguardaId, { assignedReferents: referentIds });

    if (!isDemoMode && user?.organization_id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;

        // Eliminar asignaciones previas antes de insertar las nuevas
        await db
          .from('cis_salvaguarda_assignments')
          .delete()
          .eq('salvaguarda_id', salvaguardaId);

        if (referentIds.length > 0) {
          const assignments = referentIds.map(refId => ({
            salvaguarda_id: salvaguardaId,
            referent_id: refId,
            assigned_by: user.id,
          }));
          await db.from('cis_salvaguarda_assignments').insert(assignments);
        }

        logger.info(`[CIS] ✅ Referentes asignados a salvaguarda ${salvaguardaId}`);
      } catch (err) {
        logger.error('[CIS] Error asignando referentes:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user, updateControlsState, refreshData]);

  /**
   * Agrega una nueva tarea a una salvaguarda. Genera un ID temporal local
   * para actualización optimista mientras se persiste en BD.
   *
   * @param salvaguardaId - UUID de la salvaguarda a la que se agrega la tarea.
   * @param task - Datos de la tarea (sin `id`, que se genera internamente).
   */
  const addTask = useCallback(async (
    salvaguardaId: string,
    task: Omit<CISTask, 'id'>
  ) => {
    const newTask: CISTask = {
      ...task,
      id: `cis-task-${Date.now()}`,
    };

    // Actualizar estado local inmediatamente
    setControls(prev => prev.map(ctrl => ({
      ...ctrl,
      salvaguardas: ctrl.salvaguardas.map(s =>
        s.id === salvaguardaId
          ? { ...s, tasks: [...s.tasks, newTask] }
          : s
      ),
    })));

    // Mantener selectedSalvaguarda sincronizada si corresponde
    if (selectedSalvaguarda?.id === salvaguardaId) {
      setSelectedSalvaguarda(prev =>
        prev ? { ...prev, tasks: [...prev.tasks, newTask] } : null
      );
    }

    if (!isDemoMode && user?.organization_id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;
        await db.from('cis_tasks').insert({
          salvaguarda_id: salvaguardaId,
          organization_id: user.organization_id,
          title: task.title,
          description: task.description,
          status: task.status,
          assigned_to: task.assignedTo,
          due_date: task.dueDate,
        });
      } catch (err) {
        logger.error('[CIS] Error creando tarea:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user, selectedSalvaguarda, refreshData]);

  /**
   * Actualiza campos de una tarea existente dentro de una salvaguarda.
   * Mantiene sincronizada la vista de detalle si la salvaguarda está seleccionada.
   *
   * @param salvaguardaId - UUID de la salvaguarda que contiene la tarea.
   * @param taskId - UUID de la tarea a actualizar.
   * @param updates - Campos de la tarea a modificar.
   */
  const updateTask = useCallback(async (
    salvaguardaId: string,
    taskId: string,
    updates: Partial<CISTask>
  ) => {
    setControls(prev => prev.map(ctrl => ({
      ...ctrl,
      salvaguardas: ctrl.salvaguardas.map(s =>
        s.id === salvaguardaId
          ? { ...s, tasks: s.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t) }
          : s
      ),
    })));

    if (selectedSalvaguarda?.id === salvaguardaId) {
      setSelectedSalvaguarda(prev =>
        prev
          ? { ...prev, tasks: prev.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t) }
          : null
      );
    }

    if (!isDemoMode && user?.organization_id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;
        const dbUpdates: Record<string, unknown> = {};
        if (updates.status) dbUpdates.status = updates.status;
        if (updates.title) dbUpdates.title = updates.title;
        if (updates.completedAt) dbUpdates.completed_at = updates.completedAt;
        await db.from('cis_tasks').update(dbUpdates).eq('id', taskId);
      } catch (err) {
        logger.error('[CIS] Error actualizando tarea:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user, selectedSalvaguarda, refreshData]);

  /**
   * Agrega una evidencia de cumplimiento a una salvaguarda.
   * Genera un ID temporal local para la actualización optimista.
   *
   * @param salvaguardaId - UUID de la salvaguarda a la que se agrega la evidencia.
   * @param evidence - Datos de la evidencia (sin `id`).
   */
  const addEvidence = useCallback(async (
    salvaguardaId: string,
    evidence: Omit<CISEvidence, 'id'>
  ) => {
    const newEvidence: CISEvidence = {
      ...evidence,
      id: `cis-ev-${Date.now()}`,
    };

    setControls(prev => prev.map(ctrl => ({
      ...ctrl,
      salvaguardas: ctrl.salvaguardas.map(s =>
        s.id === salvaguardaId
          ? { ...s, evidence: [...s.evidence, newEvidence] }
          : s
      ),
    })));

    if (selectedSalvaguarda?.id === salvaguardaId) {
      setSelectedSalvaguarda(prev =>
        prev ? { ...prev, evidence: [...prev.evidence, newEvidence] } : null
      );
    }

    if (!isDemoMode && user?.organization_id) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;
        await db.from('cis_evidence').insert({
          salvaguarda_id: salvaguardaId,
          organization_id: user.organization_id,
          title: evidence.fileName,
          description: evidence.description,
          file_url: evidence.fileName,
          uploaded_by: user.id,
        });
      } catch (err) {
        logger.error('[CIS] Error agregando evidencia:', err);
        await refreshData();
      }
    }
  }, [isDemoMode, user, selectedSalvaguarda, refreshData]);

  // ─────────────────────────────────────────────────────
  // VALUE
  // ─────────────────────────────────────────────────────

  const value: CISContextType = {
    controls,
    salvaguardas,
    auditTrail,
    isLoading,
    error,
    isDemoMode,
    selectedSalvaguarda,
    setSelectedSalvaguarda,
    metrics,
    updateSalvaguarda,
    assignReferents,
    addTask,
    updateTask,
    addEvidence,
    refreshData,
    getSalvaguardasByControl,
    getControlById,
    getSalvaguardaById,
    getSalvaguardasByIG,
  };

  return (
    <CISContext.Provider value={value}>
      {children}
    </CISContext.Provider>
  );
}

// =====================================================
// HOOK PERSONALIZADO
// =====================================================

/**
 * Hook para consumir el contexto CIS Controls v8 desde cualquier componente
 * hijo de `CISProvider`.
 *
 * @throws Error si se usa fuera de `CISProvider`.
 * @returns Estado completo y operaciones del framework CIS Controls.
 *
 * @example
 * const { controls, metrics, updateSalvaguarda } = useCIS();
 * const ig1Salvaguardas = getSalvaguardasByIG('IG1');
 */
export function useCIS(): CISContextType {
  const context = useContext(CISContext);
  if (context === undefined) {
    throw new Error('useCIS debe usarse dentro de un CISProvider');
  }
  return context;
}

export default CISContext;
